import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { updateSection } from "../../assignmentSlice";

import {
  convertCircledNumberToDigit,
  convertDigitToCircledNumber,
  getTextByLabel,
  ginEduMarkupToTag,
  parseChoiceAnswerList,
  parseLabeledAnswer,
} from "../../../../utils/textUtils";
import { getStartingIndex } from "../../../../utils/resumeUtils";

import BasicChoicePage from "./basic_choice/BasicChoicePage";
import MultipleChoicePage from "./multiple_choice/MultipleChoicePage";
import DescriptiveConditionPage from "./descriptive_condition/DescriptiveConditionPage";
import MultiBlankPage from "./multi_blank/MultiBlankPage";
import SummaryBlankPage from "./summary/SummaryBlankPage";
import ExamPaperPage from "./exam_paper/ExamPaperPage";

// 유형 이름을 모르는 문항을 모양으로 다듬는다(2026-10-12). 지인에듀는 새 유형을 예고 없이 연다.
// - 보기(choices)가 있으면 5지선다. (A)(B) 쌍 보기는 한 줄로 합치고, 둘째 글(text2)은 본문 뒤에 붙인다.
// - 정답이 "(A) … / (B) …" 이면 칸마다 쓰는 여러 칸 화면. 표(table)가 있으면 그 화면이 표를 그린다.
// - 나머지는 서술형.
// 유형 이름은 서버가 출제 때 텔레그램으로 알려 준다(assignment/paragraph_types.py). 알림이 오면 여기서 모양이 맞는지 본다.
const normalizeByShape = (newPage, questionIndex) => {
  const question = ginEduMarkupToTag(newPage.question);
  const textWithSecond = newPage.text2
    ? `${newPage.text ?? newPage.text1 ?? ""}\n\n${newPage.text2}`
    : newPage.text ?? newPage.text1;
  if (Array.isArray(newPage.choices) && newPage.choices.length > 0) {
    // 중등 학교 동형(school_sample · grammar_bank_pick, 2026-10-25)은 지문 없이 <보기> 상자만 text2 로 온다.
    // 빈 지문 칸을 띄우지 않고, 상자 위에 머리말(<보기>)을 붙인다.
    const boxText = String(newPage.text2 ?? "").trim()
      ? [newPage.text2_heading, newPage.text2].filter(Boolean).join("\n")
      : "";
    const choicePassage = [newPage.text ?? newPage.text1 ?? "", boxText]
      .filter((part) => String(part).trim())
      .join("\n\n");
    // 정답이 둘 이상("③, ④")이면 여러 개 고르는 화면(MultipleChoicePage)으로 간다
    const answerNumberList = parseChoiceAnswerList(newPage.answer);
    return {
      ...newPage,
      answer:
        answerNumberList.length > 1
          ? answerNumberList
          : answerNumberList[0] ?? convertCircledNumberToDigit(String(newPage.answer ?? "").trim()),
      isMultipleAnswer: answerNumberList.length > 1,
      // 여러 개 고르는 화면은 고른 번호를 배열로 쌓는다
      ...(answerNumberList.length > 1 && !Array.isArray(newPage.input) ? { input: [] } : {}),
      choices: newPage.choices.map((choice, choiceIndex) => {
        const label = Array.isArray(choice)
          ? choice.map((word, wordIndex) => `(${String.fromCharCode(65 + wordIndex)}) ${word}`).join(" · ")
          : choice;
        return `${convertDigitToCircledNumber(choiceIndex + 1)} ${ginEduMarkupToTag(label)}`;
      }),
      question,
      text: choicePassage ? ginEduMarkupToTag(choicePassage) : null,
    };
  }
  const labeledAnswerList = parseLabeledAnswer(newPage.answer);
  if (labeledAnswerList.length > 0) {
    return {
      ...newPage,
      answer: labeledAnswerList.map((one) => one.value).join(", "),
      blankLabelList: labeledAnswerList.map((one) => one.label),
      question: `${questionIndex + 1}. ${question}`,
      text: ginEduMarkupToTag(textWithSecond),
    };
  }
  return {
    ...newPage,
    question: `${questionIndex + 1}. ${question}`,
    text: ginEduMarkupToTag(newPage.text),
  };
};

const ParagraphPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();

  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const currentPage = pageList[currentIndex] || {
    type: "",
  };

  const handlePreviousPage = () => {
    if (currentIndex === 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      const isAllAnswered = pageList.every((page) => page.isSubmitted);

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    setPageList(
      section.question.data.map((question, questionIndex) => {
        const newPage = {
          ...question,
          isCorrect: false,
          isSubmitted: false,
          answerData: question.answer,
        };
        if (
          [
              "title",
              "title_high",
              "title_kor",
              "title_kor_high",
              "topic",
              "topic_high",
              "topic_kor",
              "topic_kor_high",
              "match_eng",
              "match_eng_high",
              "match_kor",
              "match_kor_high",
              "mismatch_eng",
              "mismatch_eng_high",
              "mismatch_kor",
              "mismatch_kor_high",
              "blank",
              "blank_2",
              "blank_3",
          ].includes(newPage.type)
        ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
            };
        } else if (["summary_blank_AB", "summary_blank_AB_low"].includes(newPage.type)) {
            // (A)(B) 단어쌍 선지: ["strain","response"] -> "① (A) strain · (B) response"
            return {
              ...newPage,
              // 요약문 빈칸이 "--   **(A)**   --" 처럼 공백(nbsp 포함)을 끼고 올 때가 있다(_low).
              // 화면의 빈칸 그리기는 "--**(A)**--" 만 알아보므로 공백을 걷는다
              text2: String(newPage.text2 ?? "").replace(
                /--[\s\u00a0]*\*\*\(([A-Z])\)\*\*[\s\u00a0]*--/g,
                "--**($1)**--"
              ),
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map((choice, choiceIndex) => {
                const pair = Array.isArray(choice) ? choice : [choice];
                const label = pair
                  .map(
                    (word, wordIndex) =>
                      `(${String.fromCharCode(65 + wordIndex)}) ${word}`
                  )
                  .join(" · ");
                return `${convertDigitToCircledNumber(
                  choiceIndex + 1
                )} ${label}`;
              }),
            };
        } else if (["order", "order_mid", "order_high"].includes(newPage.type)) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              text: (newPage.text ?? newPage.text1) + "\n\n" + newPage.text2,
            };
        } else if (newPage.type === "meaning_inference_eng") {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              question: ginEduMarkupToTag(newPage.question),
              text: ginEduMarkupToTag(newPage.text),
            };
        } else if (
            [
              "flow",
              "flow_2",
              "flow_4",
              "vocabulary_low",
              "grammar_low",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer[0]),
              choices: newPage.choices.map(
                (choice) => `${choice} ${getTextByLabel(newPage.text, choice)}`
              ),
              text: ginEduMarkupToTag(newPage.text),
            };
        } else if (
            [
              "vocabulary_mid",
              "vocabulary_high",
              "grammar_mid",
              "grammar_high",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer[0]),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              text: ginEduMarkupToTag(newPage.text),
            };
        } else if (
            ["position", "position_1", "position_2"].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              text: ginEduMarkupToTag(
                (newPage.text ?? newPage.text1) + "\n\n" + newPage.text2
              ),
            };
        } else if (
            [
              "match_count_eng",
              "match_count_eng_high",
              "match_count_kor",
              "match_count_kor_high",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              input: [],
              choices: (newPage.text2 ?? newPage.example)
                .split("\n")
                .map((choice) =>
                  choice.replace(/\((\d+)\)/g, (_, num) => {
                    const number = parseInt(num, 10);
                    return convertDigitToCircledNumber(number);
                  })
                ),
              answer: [...newPage.answer.matchAll(/\((\d+)\)/g)].map((match) =>
                Number(match[1])
              ),
            };
        } else if (
            [
              // [모의고사 신유형] 지인에듀가 2026-09 에 열어 준 문항들. 보기는 평범한
              // 5지선다인데 발문과 지문에 `--낱말--` 밑줄 표시가 섞여 온다.
              "vocabulary_synonym_fit",
              "vocabulary_synonym_odd",
              "vocabulary_synonym_blank",
              "vocabulary_synonym_blank_fit",
              "vocabulary_definition_choice",
              // 모두 고르기. 보기 자체가 "ⓓ, ⓔ" 처럼 라벨 묶음이라 평범한 5지선다로 낸다.
              "vocabulary_choose_all",
              "grammar_choose_all",
              // 지칭 추론(2026-10). 밑줄 친 대명사가 가리키는 것 / 가리키는 대상이 다른 것
              "referent_choice",
              "referent_odd",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              question: ginEduMarkupToTag(newPage.question),
              text: ginEduMarkupToTag(newPage.text),
            };
        } else if (
            ["vocabulary_ABC", "grammar_ABC"].includes(newPage.type)
          ) {
            // (A)(B)(C) 세 자리를 한 번에 고른다. 보기가 배열의 배열로 와서
            // `[["express","including","showcased"], ...]` 를 한 줄로 합쳐 보여 준다.
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map((choice, choiceIndex) => {
                const parts = Array.isArray(choice) ? choice : [choice];
                return `${convertDigitToCircledNumber(
                  choiceIndex + 1
                )} ${parts.join(" / ")}`;
              }),
              question: ginEduMarkupToTag(newPage.question),
              text: ginEduMarkupToTag(newPage.text),
            };
        } else if (
            [
              "descriptive_condition_low",
              "descriptive_condition_mid",
              "descriptive_condition_high",
              "descriptive_title",
              "descriptive_topic",
              "descriptive_summary",
              // [모의고사 신유형] 글에서 낱말을 찾아 쓰는 주관식. 영영풀이는 `text2`
              // 로 오고 `text2_heading` 이 "<영영풀이>" 라 조건 상자가 그대로 맞는다.
              "vocabulary_definition",
              "vocabulary_initial",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              question: `${questionIndex + 1}. ${ginEduMarkupToTag(
                newPage.question
              )}`,
              text: ginEduMarkupToTag(newPage.text),
            };
        }
        return normalizeByShape(newPage, questionIndex);
      })
    );
    setCurrentIndex(0);
  }, [section]);

  // 지인에듀 쪽 그림과 좌표가 붙은 모의고사는 문제집 그대로 푼다(exam_paper/ExamPaperPage.jsx)
  if (pageList.length > 0 && section.question?.exam_pages?.length > 0) {
    return (
      <ExamPaperPage
        isReview={isReview}
        section={section}
        pageList={pageList}
        setPageList={setPageList}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  }

  if (
    [
      "title",
      "title_high",
      "title_kor",
      "title_kor_high",
      "topic",
      "topic_high",
      "topic_kor",
      "topic_kor_high",
      "match_eng",
      "match_eng_high",
      "match_kor",
      "match_kor_high",
      "mismatch_eng",
      "mismatch_eng_high",
      "mismatch_kor",
      "mismatch_kor_high",
      "blank",
      "blank_2",
      "blank_3",
      "order",
      "flow",
      "flow_2",
      "flow_4",
      "vocabulary_low",
      "vocabulary_mid",
      "vocabulary_high",
      "position",
      "position_1",
      "position_2",
      "meaning_inference_eng",
      "grammar_low",
      "grammar_mid",
      "grammar_high",
      "summary_blank_AB",
      "vocabulary_synonym_fit",
      "vocabulary_synonym_odd",
      "vocabulary_synonym_blank",
      "vocabulary_synonym_blank_fit",
      "vocabulary_definition_choice",
      "vocabulary_choose_all",
      "grammar_choose_all",
      "vocabulary_ABC",
      "grammar_ABC",
    ].includes(currentPage.type)
  ) {
    return (
      <BasicChoicePage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (
    [
      "match_count_eng",
      "match_count_eng_high",
      "match_count_kor",
      "match_count_kor_high",
    ].includes(currentPage.type)
  ) {
    return (
      <MultipleChoicePage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (currentPage.type === "summary_blank") {
    return (
      <SummaryBlankPage
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (
    [
      // [모의고사 신유형] 낱말을 여러 개 받는 둘은 칸을 나눠 주는 화면으로 간다
      "vocabulary_definition",
      "vocabulary_initial",
    ].includes(currentPage.type)
  ) {
    return (
      <MultiBlankPage
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (
    [
      "descriptive_condition_low",
      "descriptive_condition_mid",
      "descriptive_condition_high",
      "descriptive_title",
      "descriptive_topic",
      "descriptive_summary",
    ].includes(currentPage.type)
  ) {
    return (
      <DescriptiveConditionPage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  }

  // 이름 목록에 없는 유형(지인에듀가 새로 연 것)은 모양으로 화면을 고른다. 예전에는 빈 화면이 떠서
  // 아이가 그 자리에서 막혔다(2026-10-12, 표 완성·지칭 추론·순서-상 등 220여 문항).
  const sharedProps = {
    setCurrentStep,
    isPreview,
    isReview,
    section,
    currentPage,
    currentIndex,
    pageList,
    setPageList,
    handlePreviousPage,
    handleNextPage,
  };
  if (Array.isArray(currentPage.choices) && currentPage.choices.length > 0) {
    if (currentPage.isMultipleAnswer) return <MultipleChoicePage {...sharedProps} />;
    return <BasicChoicePage {...sharedProps} />;
  }
  if (Array.isArray(currentPage.blankLabelList) && currentPage.blankLabelList.length > 0) {
    return <MultiBlankPage {...sharedProps} />;
  }
  if (currentPage.type) {
    return <DescriptiveConditionPage {...sharedProps} />;
  }
  return <div></div>;
};

export default ParagraphPage;
